import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import type { SelectedCategory } from ".";
import InfiniteScroll from "react-infinite-scroll-component";
import { t } from "@/i18n-js/instance";
import type {
  MarketingEmailTemplate,
  MarketingEmailTemplateContentType,
} from "@/react/types/MarketingEmailTemplates";
import { Typography } from "@circle-react-uikit/Typography";
import { LoaderRow } from "../../../LoaderRow";
import { TemplateCardV2 } from "../TemplateContentElements/TemplateCardV2";
import { getResponsiveGridClasses } from "../hooks/useTemplateCardSize";
import { Placeholder } from "./Placeholder";
import type { ContentType } from "./TemplateFilter";
import { TemplateFilter } from "./TemplateFilter";

const i18nRoot = "settings.emails.templates";

export const TemplateList = ({
  scrollableNodeIdRef,
  fetchNextPage,
  hasNextPage,
  templates,
  totalCount,
  onApply,
  selectedContentType,
  setSelectedContentType,
  selectedCategory,
}: {
  scrollableNodeIdRef: MutableRefObject<string>;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  templates: MarketingEmailTemplate[];
  totalCount: number;
  selectedContentType: ContentType;
  setSelectedContentType: Dispatch<SetStateAction<ContentType>>;
  onApply: (
    templateId: number,
    contentType: MarketingEmailTemplateContentType,
  ) => void;
  selectedCategory: SelectedCategory;
}) => {
  const shouldShowFilter = selectedCategory !== "circle_templates";

  return (
    <>
      {shouldShowFilter && (
        <TemplateFilter
          selectedContentType={selectedContentType}
          setSelectedContentType={setSelectedContentType}
        />
      )}
      {totalCount === 0 ? (
        <Placeholder
          title={t([i18nRoot, "no_templates_title"])}
          description={t([i18nRoot, "no_templates_description"])}
        />
      ) : (
        <>
          <Typography.LabelXs weight="medium">
            {t([i18nRoot, "saved_templates_count"], {
              count: totalCount,
            })}
          </Typography.LabelXs>
          <div className="flex-1 pb-6" id={scrollableNodeIdRef.current}>
            <InfiniteScroll
              next={fetchNextPage}
              hasMore={Boolean(hasNextPage)}
              loader={<LoaderRow />}
              dataLength={templates.length}
              scrollableTarget={scrollableNodeIdRef.current}
            >
              <div
                className={`grid ${getResponsiveGridClasses()} justify-start gap-8`}
              >
                {templates.map(template => (
                  <TemplateCardV2
                    key={template.id}
                    template={template}
                    onApply={onApply}
                  />
                ))}
              </div>
            </InfiniteScroll>
          </div>
        </>
      )}
    </>
  );
};
